import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";

import "antd/dist/antd.variable.min.css";
import { IS_DEV_ENV } from "constants/app.constants";
import initStore from "store/store";
import { uid } from "utils";
import { initCrashDetector } from "utils/crashDetector";
import { baseRedirectTarget } from "utils/publicUrl";
// Initialize Sentry as early as possible
import { SentryErrorBoundary, initSentry, logger } from "utils/sentry";
// Initialize Sentry axios interceptors for global API monitoring
import "utils/sentryAxiosInterceptors";

// Registers the interceptor that sends X-Display-Unit to the services that read the unit. Imported
// here on purpose: its other importers use it for values, and could stop importing it.
import "api/displayUnitHeader";
import { ThemeProvider } from "api/theme.context";

import { UserProvider } from "components/user/context";

import App from "./App";
import "./antd.css";
import "./index.css";
import "./main.scss";
import reportWebVitals from "./reportWebVitals";
import "./wdyr";

initSentry();
initCrashDetector();

const render = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  let channel = urlParams.get("channel");
  if (!channel) {
    channel = uid();
  }
  const store = initStore({ channel });
  if (IS_DEV_ENV) {
    void import("utils/qa/evaQA").then(({ installEvaQA }) => installEvaQA(store));
  }
  const rootEl = document.getElementById("root");

  // eslint-disable-next-line import/no-named-as-default-member
  ReactDOM.render(
    <Provider store={store}>
      <React.StrictMode>
        <SentryErrorBoundary
          fallback={({ error }) => {
            logger.error("Error boundary caught:", {
              error: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined
            });
            return (
              <UserProvider>
                <ThemeProvider>
                  <App />
                </ThemeProvider>
              </UserProvider>
            );
          }}>
          <UserProvider>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </UserProvider>
        </SentryErrorBoundary>
      </React.StrictMode>
    </Provider>,
    rootEl
  );
};

// Before anything renders, so the router and asset links see the base.
const baseRedirect = baseRedirectTarget(window.location);
if (baseRedirect) {
  window.location.replace(baseRedirect);
} else {
  render();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// eslint-disable-next-line no-console
reportWebVitals(console.log);
